// @erp/ui/error-card — what a region shows when its request FAILED, and what a
// screen shows when the record (or the route) does not exist.
//
// WHY IT EXISTS (audit UX-02 / UX-03 / UX-10, 2026-09-16). The kit had an
// EmptyState and a Skeleton but nothing for the third outcome of a fetch, so a
// 500 rendered as "No pending claims." on 58 of 59 DataTable screens, stat rows
// pulsed forever, a missing CMS record came up as an editable empty form with a
// Publish button, and the same record 404 wore four treatments across lib
// (white card, warning banner, bare "Not Found", silence). These two cards are
// that outcome, decided once:
//
//   ErrorCard      reads the failure's HTTP status and says the right thing —
//                  404 → NotFoundCard; 401 / 403 → "You do not have access to
//                  this"; anything else → "Something went wrong" + Retry.
//                  role="alert": a failed request is an event the reader must
//                  hear, unlike an empty list.
//   NotFoundCard   the record or the route is gone: an h2 (h1 in `heading`
//                  mode, when the card IS the page), one sentence, a primary
//                  "Back to …" and a secondary "Home" — always a way out.
//   ModuleUnavailableCard
//                  the route exists but its MODULE is not part of this install
//                  (ERP-DEPLOYMENT-MODEL §13, 2026-09-20): the shell renders it
//                  in place of the screen when the capabilities manifest omits
//                  the module the URL belongs to. Same anatomy, one Home link.
//                  Distinct from not-found on purpose — a bookmarked library
//                  page on a CMS-only install is not a broken link, it is a
//                  module the installation was set up without, and the copy
//                  says who to ask.
//
// THE SURFACE. A resting card — layer 2 (bg-base-100) + the 1px gray-200
// decorative edge (border-base-300), rounded-500, no shadow (DEV-GUIDE-002
// §12.1) — carrying the NEGATIVE two-tone capsule: red-100 opaque ground +
// red-900 ink (`bg-error-tint text-error`, §12.4, measured 4.71:1 light /
// 6.18 darkest), the same tile geometry EmptyState draws in the accent pair
// (rounded-800, 56px / 44px compact). The capsule is the ONLY hue on the card:
// the title is a heading (gray-900, §2b), the sentence is gray-700
// (text-base-muted), and no running text turns red (§12.4 D4 — coloured text
// is links and owed amounts only). The Retry / Back button is the accent, the
// one action the card is for; Home is its grey companion.
//
// WHAT IT NEVER SHOWS: the raw error. `error.message` on the wire is
// "Request failed (500)" or a code-shaped server string — an operator reading
// "D1_ERROR: no such column" learns nothing they can act on. The card speaks in
// the reader's terms and leaves the message to the console and the audit log.
//
// THE STATUS READ is structural, not an `instanceof`. @erp/ui may not import
// @erp/api-client (ADR-001 Fig 4.1 Lane 4: the design system is domain-free),
// so the card reads `{ status: number }` off whatever it is handed — which
// ApiError carries, and a bare `Response` would too. Anything without a
// numeric status is "something went wrong", the honest default.
import { Show, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { Link } from '@tanstack/solid-router';
import { FileQuestion, Lock, Puzzle, RotateCcw, TriangleAlert } from 'lucide-solid';
import { Button, buttonClasses } from '../button/index.js';

/** The three things a failed request can mean to the reader. */
export type ErrorKind = 'not-found' | 'forbidden' | 'failed';

/** The HTTP status on a thrown value, when it carries one (`ApiError.status`, `Response.status`). */
export function httpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/** Classify a failure by its status: 404 → not-found, 401/403 → forbidden, everything else (including no status) → failed. */
export function errorKind(error: unknown): ErrorKind {
  const status = httpStatus(error);
  if (status === 404) return 'not-found';
  if (status === 401 || status === 403) return 'forbidden';
  return 'failed';
}

/* The frame both cards share — EmptyState's centred column, on a resting card. */
const FRAME = 'flex flex-col items-center justify-center gap-3 text-center rounded-500 border border-base-300 bg-base-100';
const PAD = { full: 'px-6 py-16', compact: 'px-4 py-10' } as const;
/* The negative capsule: EmptyState's tile geometry in the error pair (§12.4). */
const CAPSULE = { full: 'h-14 w-14', compact: 'h-11 w-11' } as const;
const GLYPH = { full: 24, compact: 18 } as const;

/** The capsule, the title (h1 / h2 / h3) and the sentence — the anatomy both cards draw. */
function CardBody(props: {
  icon: Component<{ size?: number; class?: string }>;
  title: string;
  heading?: boolean;
  compact?: boolean;
  children?: JSX.Element;
}) {
  const density = () => (props.compact ? 'compact' : 'full');
  return (
    <>
      <div
        class={`grid shrink-0 place-items-center rounded-800 bg-error-tint text-error ${CAPSULE[density()]}`}
        aria-hidden="true"
      >
        <Dynamic component={props.icon} size={GLYPH[density()]} />
      </div>
      <div>
        {/* h1 when the card IS the page (the router's not-found, a detail
            screen whose record is gone) so every screen keeps one h1; h2
            otherwise — the card replaces a region's content, and a region's
            title sits one level under the page's. Compact drops to h3, the
            level EmptyState uses inside a panel. No colour or weight utility on
            any of them (§12.2 D17 / D5: spectrum.css §2b paints headings). */}
        <Show
          when={props.heading}
          fallback={
            <Show when={props.compact} fallback={<h2 class="text-lg">{props.title}</h2>}>
              <h3 class="text-base">{props.title}</h3>
            </Show>
          }
        >
          <h1 class="text-2xl leading-tight tracking-tight">{props.title}</h1>
        </Show>
        <Show when={props.children}>
          <p class="mx-auto mt-1 max-w-[420px] text-sm leading-relaxed text-base-muted">{props.children}</p>
        </Show>
      </div>
    </>
  );
}

export interface NotFoundCardProps {
  /** The noun, singular and lower-case — "announcement", "title", "page": the title reads "This announcement could not be found". */
  what: string;
  /** The primary way out — the list this record belonged to (`{ label: "Back to announcements", to: "/cms/announcements" }`). A router route, so the link preloads like any other. */
  backTo?: { label: string; to: string };
  /** Also offer a secondary "Home" link to `/`. Default: on when `backTo` is absent (there is always a way out), off otherwise. */
  home?: boolean;
  /** Render the title as the page's h1 — for a card that IS the page (the router's not-found, a detail screen whose record is gone). Default h2. */
  heading?: boolean;
  /** Tighter padding and a smaller capsule for a card inside a panel or a table body. */
  compact?: boolean;
  /** One sentence under the title. Default: "It may have been deleted, or the link you followed is out of date." */
  hint?: string;
  /** Automation id on the card; the links are `${id}-back` and `${id}-home`. Required — a not-found is what a test asserts appeared. */
  id: string;
}

/**
 * The record or the route does not exist. One card for every detail / edit
 * screen and for the router's `defaultNotFoundComponent`: the negative
 * capsule, "This <what> could not be found", a primary "Back to …" link and
 * an optional secondary Home. Render it INSTEAD of the screen — never beside
 * a form or an action bar for a record that is not there.
 */
export function NotFoundCard(props: NotFoundCardProps) {
  const showHome = () => props.home ?? props.backTo === undefined;
  return (
    <div id={props.id} role="alert" data-kind="not-found" class={`${FRAME} ${PAD[props.compact ? 'compact' : 'full']}`}>
      <CardBody icon={FileQuestion} title={`This ${props.what} could not be found`} heading={props.heading} compact={props.compact}>
        {props.hint ?? 'It may have been deleted, or the link you followed is out of date.'}
      </CardBody>
      <Show when={props.backTo || showHome()}>
        <div class="mt-1 flex flex-wrap items-center justify-center gap-2">
          <Show when={props.backTo}>
            {(back) => (
              /* A router Link wearing the button recipe — one tab stop, one
                 element; an <a> around a <Button> would be two (audit rank 6). */
              <Link id={`${props.id}-back`} to={back().to} class={buttonClasses({ variant: 'primary' })} data-variant="primary">
                {back().label}
              </Link>
            )}
          </Show>
          <Show when={showHome()}>
            <Link
              id={`${props.id}-home`}
              to="/"
              class={buttonClasses({ variant: props.backTo ? 'secondary' : 'primary' })}
              data-variant={props.backTo ? 'secondary' : 'primary'}
            >
              Home
            </Link>
          </Show>
        </div>
      </Show>
    </div>
  );
}

export interface ModuleUnavailableCardProps {
  /** Automation id on the card; the Home link is `${id}-home`. Required — an unavailable module is what a test asserts appeared. */
  id: string;
  /** The module's human label, lower-case as `MODULE_LABELS` spells it ("library", "gate desk"): the title capitalises it, the hint drops it mid-sentence. */
  moduleLabel: string;
  /** Render the title as the page's h1 — the shell renders this card IN PLACE of the route, so it IS the page. Default h2. */
  heading?: boolean;
  /** Where the Home link points. Default `/`. */
  homeTo?: string;
}

/** "library" → "Library": the label is stored lower-case for mid-sentence use; a title starts the sentence. */
const capitalise = (s: string): string => (s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1));

/**
 * The route belongs to a module this installation was set up without
 * (ERP-DEPLOYMENT-MODEL §13). The shell renders it in place of the screen —
 * inside the chrome, so the sidebar stays and the person can go elsewhere —
 * when the capabilities manifest omits the module the URL resolves to. One
 * Home link as the way out; no Retry, because nothing the reader does changes
 * an install's module set (a change is a deploy).
 */
export function ModuleUnavailableCard(props: ModuleUnavailableCardProps) {
  return (
    <div id={props.id} role="alert" data-kind="module-unavailable" class={`${FRAME} ${PAD.full}`}>
      <CardBody icon={Puzzle} title={`${capitalise(props.moduleLabel)} isn't part of this installation`} heading={props.heading}>
        This ERP was set up without the {props.moduleLabel} module. If you expected it, ask whoever administers the
        installation.
      </CardBody>
      <div class="mt-1 flex flex-wrap items-center justify-center gap-2">
        <Link id={`${props.id}-home`} to={props.homeTo ?? '/'} class={buttonClasses({ variant: 'primary' })} data-variant="primary">
          Home
        </Link>
      </div>
    </div>
  );
}

export interface ErrorCardProps {
  /** The thrown value — usually `query.error`. Its `status`, when it has one, picks the card. */
  error: unknown;
  /** Re-run the request — usually `() => void query.refetch()`. Rendered as the Retry button (`${id}-retry`) on a failed request; a 403 and a 404 offer no retry. */
  onRetry?: () => void;
  /** Override the headline — "The ledger could not be loaded" instead of "Something went wrong". Never the error's own message. */
  title?: string;
  /** Tighter padding and a smaller capsule for a card inside a panel, a table body or a stat row. */
  compact?: boolean;
  /** Render the title as the page's h1 — for a card that IS the page (a detail screen whose one request failed). */
  heading?: boolean;
  /** What a 404 is about, so the delegated NotFoundCard can say "This announcement could not be found · Back to announcements". Without it a 404 still reads as not found, with Home as the way out. */
  notFound?: Pick<NotFoundCardProps, 'what' | 'backTo' | 'home' | 'hint'>;
  /** Automation id on the card; Retry is `${id}-retry`. Required — an error card is what a test asserts appeared. */
  id: string;
}

/**
 * A failed request, in the reader's terms. Reads the status off `error`:
 * 404 renders NotFoundCard; 401 / 403 says the account lacks access and
 * offers no retry; everything else says something went wrong and offers
 * Retry. DataTable, ListView and the stat row render it for you when handed
 * `error`; detail screens render it in place of the skeleton on `isError`.
 */
export function ErrorCard(props: ErrorCardProps) {
  const kind = () => errorKind(props.error);
  return (
    <Show
      when={kind() !== 'not-found'}
      fallback={
        <NotFoundCard
          id={props.id}
          what={props.notFound?.what ?? 'record'}
          backTo={props.notFound?.backTo}
          home={props.notFound?.home}
          hint={props.notFound?.hint}
          heading={props.heading}
          compact={props.compact}
        />
      }
    >
      <div id={props.id} role="alert" data-kind={kind()} class={`${FRAME} ${PAD[props.compact ? 'compact' : 'full']}`}>
        <Show
          when={kind() === 'forbidden'}
          fallback={
            <>
              <CardBody icon={TriangleAlert} title={props.title ?? 'Something went wrong'} heading={props.heading} compact={props.compact}>
                The request did not complete. It is not something you did — try again, and if it keeps happening tell
                an administrator.
              </CardBody>
              <Show when={props.onRetry}>
                {(retry) => (
                  <div class="mt-1">
                    <Button id={`${props.id}-retry`} variant="primary" onClick={() => retry()()}>
                      <RotateCcw size={14} aria-hidden="true" />
                      Retry
                    </Button>
                  </div>
                )}
              </Show>
            </>
          }
        >
          <CardBody icon={Lock} title={props.title ?? 'You do not have access to this'} heading={props.heading} compact={props.compact}>
            Your account does not hold the permission this needs. Ask an administrator to grant it, or sign in with
            another account.
          </CardBody>
        </Show>
      </div>
    </Show>
  );
}
